import type { Unsubscribe } from 'firebase/firestore';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { classifyAuthOperationError } from 'src/auth/errors/auth-operation-error';
import {
  getSessionGeneration,
  registerSessionCleanup,
  isSessionGenerationCurrent,
} from 'src/auth/session-lifecycle';

import { loadInventory } from './inventory-loader';
import { subscribeBusinessDocumentSnapshots } from './business-document-snapshots';

const retries = new Map<string, ReturnType<typeof setTimeout>>();
const cooldowns = new Map<string, number>();
const latestTargets = new Map<string, number>();
registerSessionCleanup(() => {
  retries.forEach(clearTimeout);
  retries.clear();
  cooldowns.clear();
  latestTargets.clear();
});

function normalizeFirestoreDate(value: unknown): Date {
  if (value && typeof value === 'object' && 'toDate' in value)
    return (value as { toDate: () => Date }).toDate();
  return value instanceof Date ? value : new Date(value as string);
}

export async function handleInventoryBusinessField(
  inventory: unknown,
  sourceBusinessId?: string,
  retryAttempt = 0
): Promise<void> {
  if (!inventory || typeof inventory !== 'object') return;
  const inv = inventory as {
    version?: number;
    updatedAt?: unknown;
    earliestChangedAt?: unknown;
    forceFull?: boolean;
    fullReconcileVersion?: number;
  };
  if (typeof inv.version !== 'number' || !Number.isFinite(inv.version)) return;
  const businessId = sourceBusinessId ?? useDeviceSyncStore.getState().businessData?.id;
  if (!businessId || useDeviceSyncStore.getState().businessData?.id !== businessId) return;
  const generation = getSessionGeneration();
  const latest = latestTargets.get(businessId);
  if (latest !== undefined && inv.version < latest) return;
  latestTargets.set(businessId, inv.version);
  if (Date.now() < (cooldowns.get(businessId) ?? 0)) return;
  clearTimeout(retries.get(businessId));
  retries.delete(businessId);
  try {
    await loadInventory(businessId, {
      version: inv.version,
      updatedAt: normalizeFirestoreDate(inv.updatedAt),
      earliestChangedAt: normalizeFirestoreDate(inv.earliestChangedAt),
      forceFull: inv.forceFull === true,
      fullReconcileVersion: inv.fullReconcileVersion,
    });
    clearTimeout(retries.get(businessId));
    retries.delete(businessId);
  } catch (error) {
    if (
      !isSessionGenerationCurrent(generation) ||
      useDeviceSyncStore.getState().businessData?.id !== businessId
    )
      return;
    const failure = classifyAuthOperationError(error);
    if (failure.kind === 'RATE_LIMIT') {
      cooldowns.set(businessId, Date.now() + (failure.retryAfterMs ?? 60_000));
    }
    const canRetry = ['NETWORK', 'TIMEOUT', 'SERVER'].includes(failure.kind) && retryAttempt < 2;
    console.error('[InventorySync] Sync failed:', error);
    clearTimeout(retries.get(businessId));
    if (canRetry && latestTargets.get(businessId) === inv.version) {
      retries.set(
        businessId,
        setTimeout(() => {
          handleInventoryBusinessField(inventory, businessId, retryAttempt + 1);
        }, [2_000, 10_000][retryAttempt])
      );
    }
  }
}

export function startInventorySyncListener(businessId: string): Unsubscribe {
  if (!businessId) return () => {};
  return subscribeBusinessDocumentSnapshots(
    businessId,
    (snapshot) => {
      if (snapshot.exists) handleInventoryBusinessField(snapshot.data?.inventory, businessId);
    },
    (error) => console.error('[InventorySync] Listener error:', error)
  );
}
